import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { storage } from "../firebase/config";
import { fileToBase64 } from "../utils/fileToBase64";

/**
 * File category definitions and their configurations
 */
export const FILE_TYPES = {
  PROFILE: "profiles",
  INVOICE: "invoices",
  PAYMENT: "payments",
  PRODUCT: "products",
  BANNER: "banners",
};

const CONFIG = {
  [FILE_TYPES.PROFILE]: {
    path: (id) => `uploads/profiles/${id}/`,
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    maxSizeMB: 2,
    label: "Customer Profile Image",
  },
  [FILE_TYPES.INVOICE]: {
    path: (id) => `uploads/invoices/${id}/`,
    allowedTypes: ["application/pdf", "image/jpeg", "image/png"],
    maxSizeMB: 5,
    label: "Invoice PDF/Image",
  },
  [FILE_TYPES.PAYMENT]: {
    path: (id) => `uploads/payments/${id}/`,
    allowedTypes: ["application/pdf", "image/jpeg", "image/png"],
    maxSizeMB: 5,
    label: "Payment Receipt",
  },
  [FILE_TYPES.PRODUCT]: {
    path: (id) => `uploads/products/${id}/`,
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    maxSizeMB: 5,
    label: "Product Image",
  },
  [FILE_TYPES.BANNER]: {
    path: () => `uploads/banners/`,
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    maxSizeMB: 5,
    label: "Marketing Banner",
  },
};

/**
 * Validates a file based on its type configuration
 * @param {File} file 
 * @param {string} fileType 
 * @returns {string|null} Error message or null if valid
 */
const validateFile = (file, fileType) => {
  const config = CONFIG[fileType];
  if (!config) return "Invalid file category provided.";

  // Type validation
  if (!config.allowedTypes.includes(file.type)) {
    return `Invalid file type for ${config.label}. Allowed: ${config.allowedTypes.join(", ")}`;
  }

  // Size validation
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > config.maxSizeMB) {
    return `File too large. Maximum size for ${config.label} is ${config.maxSizeMB}MB.`;
  }

  return null;
};

/**
 * Uploads a file to Firebase Storage with structured path and Base64 fallback.
 * @param {File} file - The file object to upload
 * @param {string} fileType - One of FILE_TYPES constants
 * @param {string} id - Relevant ID (customerId, invoiceId, etc.)
 * @param {string} userRole - The role of the current user for validation
 * @returns {Promise<string>} The download URL or Base64 Data URL string
 */
export const uploadFile = async (file, fileType, id = "default", userRole = "ADMIN") => {
  // 1. Role validation if role provided
  if (userRole) {
    const allowedRoles = ["ADMIN", "OWNER", "SALES_EXECUTIVE"];
    if (!allowedRoles.includes(userRole.toUpperCase())) {
      throw new Error("Unauthorized: Access denied for your role.");
    }

    if (userRole.toUpperCase() === "SALES_EXECUTIVE") {
      const allowedForSales = [FILE_TYPES.INVOICE, FILE_TYPES.PAYMENT];
      if (!allowedForSales.includes(fileType)) {
        throw new Error("Permission Denied: Sales Executives can only upload Invoices and Payment Receipts.");
      }
    }
  }

  // 2. File validation
  const validationError = validateFile(file, fileType);
  if (validationError) throw new Error(validationError);

  // 3. Attempt Firebase upload first
  try {
    const config = CONFIG[fileType];
    const folderPath = config.path(id);
    const extension = file.name.split(".").pop();
    const fileName = `${Date.now()}-${uuidv4()}.${extension}`;
    const fullPath = `${folderPath}${fileName}`;

    const storageRef = ref(storage, fullPath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log(`Successfully uploaded ${file.name} to Firebase Storage (${fullPath})`);
    return downloadURL;

  } catch (error) {
    // Firebase failed (CORS, network preflight, offline, permission denied, etc.)
    console.warn(`Firebase Upload Warning for ${file.name} (${error.message}). Switching seamlessly to Base64 Data URL fallback.`);
    
    try {
      // Fallback to Base64 Data URL
      const base64DataUrl = await fileToBase64(file);
      console.log(`Successfully converted ${file.name} to Base64 Data URL fallback.`);
      return base64DataUrl;
    } catch (b64Error) {
      console.error("Critical: Both Firebase Storage upload and Base64 conversion failed.", b64Error);
      throw new Error(`Upload and fallback conversion failed: ${b64Error.message}`);
    }
  }
};

/**
 * Utility helper to upload product images with Base64 Data URL fallback.
 * @param {File|string} fileOrString - File object or existing string URL
 * @param {string|number} productId - Product ID or "new"
 * @returns {Promise<string>} Firebase URL or Base64 Data URL
 */
export const uploadImage = async (fileOrString, productId = "new") => {
  if (!fileOrString) return "";
  if (typeof fileOrString === "string") return fileOrString; // Already a URL or Base64 string

  if (fileOrString instanceof File) {
    return await uploadFile(fileOrString, FILE_TYPES.PRODUCT, productId);
  }

  return "";
};
